import type { ApiResponse } from "../../../shared/types/api";
import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";
import type {
  OrderRecognitionRule,
  CustomerEmailItem,
  CustomerSuggestion,
  SaveOrderRecognitionRuleRequest,
  AddCustomerEmailRequest,
} from "../../../domain/types/order-recognition-rule";

export class OrderRecognitionRuleApiService {
  /** 규칙 + 키워드 + 등록 고객 메일 통합 조회 */
  async getRule(
    companyId: number,
  ): Promise<ApiResponse<OrderRecognitionRule>> {
    const response = await httpClient.get<ApiResponse<OrderRecognitionRule>>(
      NEW_API_ENDPOINTS.orderRecognitionRuleController.GET.rule(companyId),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "주문 인식 규칙을 가져오지 못했습니다.",
      );
    }

    return response.data;
  }

  /** 규칙 저장 (키워드 일괄 교체) */
  async saveRule(
    companyId: number,
    request: SaveOrderRecognitionRuleRequest,
  ): Promise<ApiResponse<OrderRecognitionRule>> {
    const response = await httpClient.put<ApiResponse<OrderRecognitionRule>>(
      NEW_API_ENDPOINTS.orderRecognitionRuleController.PUT(companyId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "주문 인식 규칙 저장에 실패했습니다.",
      );
    }

    return response.data;
  }

  /** 등록 고객 메일 목록 */
  async getCustomerEmails(
    companyId: number,
  ): Promise<ApiResponse<CustomerEmailItem[]>> {
    const response = await httpClient.get<ApiResponse<CustomerEmailItem[]>>(
      NEW_API_ENDPOINTS.orderRecognitionRuleController.GET.customerEmails(
        companyId,
      ),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "등록 고객 메일을 가져오지 못했습니다.",
      );
    }

    return response.data;
  }

  /** 등록 고객 메일 추가 (기존 유저 연결 또는 게스트 생성) */
  async addCustomerEmail(
    companyId: number,
    request: AddCustomerEmailRequest,
  ): Promise<ApiResponse<CustomerEmailItem>> {
    const response = await httpClient.post<ApiResponse<CustomerEmailItem>>(
      NEW_API_ENDPOINTS.orderRecognitionRuleController.POST(companyId),
      request,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "등록 고객 메일 추가에 실패했습니다.",
      );
    }

    return response.data;
  }

  /** 등록 고객 메일 삭제 */
  async deleteCustomerEmail(
    companyId: number,
    id: number,
  ): Promise<ApiResponse<void>> {
    const response = await httpClient.delete<ApiResponse<void>>(
      NEW_API_ENDPOINTS.orderRecognitionRuleController.DELETE(companyId, id),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "등록 고객 메일 삭제에 실패했습니다.",
      );
    }

    return response.data;
  }

  /** 이메일 자동완성 추천 (회사 내 기존 유저) */
  async suggestCustomerEmails(
    companyId: number,
    email: string,
  ): Promise<ApiResponse<CustomerSuggestion[]>> {
    const response = await httpClient.get<ApiResponse<CustomerSuggestion[]>>(
      NEW_API_ENDPOINTS.orderRecognitionRuleController.GET.suggestCustomerEmails(
        companyId,
        email,
      ),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "이메일 추천을 가져오지 못했습니다.",
      );
    }

    return response.data;
  }
}

export const orderRecognitionRuleApiService =
  new OrderRecognitionRuleApiService();
